// The project's title, with a pencil to set the operator's friendly name in place.
// The BMA name stays the project's name of record; this only changes what is shown.
import { useState } from "react";

import {
  FRIENDLY_NAME_MAX_CHARS,
  useSetProjectFriendlyName,
} from "../api/hooks/useProjectFriendlyName";
import styles from "./ProjectTitle.module.css";

export const ProjectTitle = ({
  projectId,
  title,
  friendlyName,
  titleClassName,
}: {
  projectId: string;
  title: string;
  // Null while the project is loading: there is nothing to edit yet.
  friendlyName: string | null | undefined;
  titleClassName: string;
}) => {
  const [draft, setDraft] = useState<string | null>(null);
  const save = useSetProjectFriendlyName(projectId);
  const saving = save.isLoading;

  const submit = (friendly_name: string | null) =>
    save.mutate({ friendly_name }, { onSuccess: () => setDraft(null) });

  const editing = draft !== null;
  const trimmed = (draft ?? "").trim();
  const tooLong = trimmed.length > FRIENDLY_NAME_MAX_CHARS;
  const canSave = trimmed.length > 0 && !tooLong && !saving;
  return (
    <div className={styles.titleBlock}>
      {/* The pencil sits inside the heading so it follows the last word of a name
          that wraps, rather than the edge of the heading's box. */}
      <h1 className={titleClassName}>
        {title}
        {!editing && friendlyName !== undefined && (
          <button
            type="button"
            className={styles.pencil}
            aria-label="Edit friendly name"
            title="Edit friendly name"
            onClick={() => {
              save.reset();
              setDraft(friendlyName ?? "");
            }}
          >
            <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
              <path
                d="M11.1 1.9a1.5 1.5 0 0 1 2.1 0l.9.9a1.5 1.5 0 0 1 0 2.1L5.6 13.4 2 14l.6-3.6 8.5-8.5Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </h1>
      {editing && (
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            if (canSave) submit(trimmed);
          }}
        >
          <input
            className={styles.input}
            value={draft}
            autoFocus
            disabled={saving}
            aria-label="Friendly name"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setDraft(null);
            }}
          />
          <button type="submit" className={styles.primary} disabled={!canSave}>
            {saving ? "Saving..." : "Save"}
          </button>
          <button
            type="button"
            className={styles.button}
            disabled={saving}
            onClick={() => setDraft(null)}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.button}
            disabled={saving || !friendlyName}
            title={
              friendlyName
                ? "Show the BMA name again"
                : "No friendly name is set"
            }
            onClick={() => submit(null)}
          >
            Clear
          </button>
          {tooLong && (
            <span className={styles.error}>
              At most {FRIENDLY_NAME_MAX_CHARS} characters
            </span>
          )}
          {save.isError && (
            <span className={styles.error}>
              Couldn't save: {save.error.message}
            </span>
          )}
        </form>
      )}
    </div>
  );
};
